import React, { useState, useEffect } from 'react';
import { 
  CorrectionInvoice, 
  CorrectionInvoiceStatus, 
  CorrectionType 
} from '../types/correctionInvoice';
import { correctionInvoiceService } from '../services/correctionInvoiceService';
import { useTranslation } from '../hooks/useTranslation';
import { Language, PDF_LANGUAGE_OPTIONS } from '../types/language';
import { formatCurrency } from '../utils/currency';
import { useLanguage } from '../contexts/LanguageContext';

interface CorrectionInvoiceListProps {
  companyId: string;
  originalInvoiceId?: string;
}

const CorrectionInvoiceList: React.FC<CorrectionInvoiceListProps> = ({
  companyId,
  originalInvoiceId
}) => {
  const { t } = useTranslation();
  const [correctionInvoices, setCorrectionInvoices] = useState<CorrectionInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedCorrectionInvoice, setSelectedCorrectionInvoice] = useState<CorrectionInvoice | null>(null);

  useEffect(() => {
    if (originalInvoiceId) {
      loadCorrectionInvoicesByOriginalInvoice();
    } else {
      loadCorrectionInvoices();
    }
  }, [companyId, originalInvoiceId, currentPage]);

  const loadCorrectionInvoices = async () => {
    try {
      setLoading(true);
      const response = await correctionInvoiceService.getCorrectionInvoices(companyId, currentPage, 20);
      setCorrectionInvoices(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      setError(t('correctionInvoices.noCorrectionsFound'));
      console.error('Error loading correction invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCorrectionInvoicesByOriginalInvoice = async () => {
    if (!originalInvoiceId) return;

    try {
      setLoading(true);
      const invoices = await correctionInvoiceService.getCorrectionInvoicesByOriginalInvoice(companyId, originalInvoiceId);
      setCorrectionInvoices(invoices);
      setTotalPages(1);
      setTotalElements(invoices.length);
    } catch (err) {
      setError(t('correctionInvoices.noCorrectionsFound'));
      console.error('Error loading correction invoices by original invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (correctionInvoiceId: string, newStatus: CorrectionInvoiceStatus) => {
    try {
      await correctionInvoiceService.updateStatus(companyId, correctionInvoiceId, newStatus);
      // Перезагружаем список
      if (originalInvoiceId) {
        loadCorrectionInvoicesByOriginalInvoice();
      } else {
        loadCorrectionInvoices();
      }
    } catch (err) {
      setError(t('correctionInvoices.errors.updateStatus'));
      console.error('Error updating status:', err);
    }
  };

  const handleDelete = async (correctionInvoiceId: string) => {
    if (!window.confirm(t('correctionInvoices.deleteConfirm'))) {
      return;
    }

    try {
      await correctionInvoiceService.deleteCorrectionInvoice(companyId, correctionInvoiceId);
      // Перезагружаем список
      if (originalInvoiceId) {
        loadCorrectionInvoicesByOriginalInvoice();
      } else {
        loadCorrectionInvoices();
      }
    } catch (err) {
      setError(t('correctionInvoices.errors.deleteError'));
      console.error('Error deleting correction invoice:', err);
    }
  };

  const handleDownloadPdf = async (correctionInvoiceId: string, correctionNumber: string, clientName: string, language: Language = Language.PL_ONLY) => {
    try {
      await correctionInvoiceService.downloadCorrectionInvoicePdf(companyId, correctionInvoiceId, correctionNumber, clientName, language);
    } catch (err) {
      setError(t('correctionInvoices.errors.downloadError'));
      console.error('Error downloading PDF:', err);
    }
  };

  const handleViewPdf = (correctionInvoice: CorrectionInvoice) => {
    setSelectedCorrectionInvoice(correctionInvoice);
    setPdfViewerOpen(true);
  };

  const getStatusColor = (status: CorrectionInvoiceStatus) => {
    switch (status) {
      case CorrectionInvoiceStatus.DRAFT:
        return 'bg-gray-100 text-gray-800';
      case CorrectionInvoiceStatus.SENT:
        return 'bg-blue-100 text-blue-800';
      case CorrectionInvoiceStatus.ACCEPTED:
        return 'bg-green-100 text-green-800';
      case CorrectionInvoiceStatus.REJECTED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: CorrectionInvoiceStatus) => {
    return t(`correctionInvoices.statuses.${status}`);
  };

  const getTypeText = (type: CorrectionType) => {
    return t(`correctionInvoices.types.${type}`);
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => {
            setError('');
            if (originalInvoiceId) {
              loadCorrectionInvoicesByOriginalInvoice();
            } else {
              loadCorrectionInvoices();
            }
          }}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('correctionInvoices.tryAgain')}
        </button>
      </div>
    );
  }

  if (correctionInvoices.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{t('correctionInvoices.noCorrectionsFound')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {t('correctionInvoices.title')}
            {originalInvoiceId && correctionInvoices[0]?.originalInvoiceNumber && ` (${correctionInvoices[0]?.originalInvoiceNumber})`}
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('correctionInvoices.tableHeaders.number')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('correctionInvoices.tableHeaders.date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('correctionInvoices.tableHeaders.type')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('correctionInvoices.tableHeaders.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('correctionInvoices.tableHeaders.difference')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('correctionInvoices.tableHeaders.reason')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('correctionInvoices.tableHeaders.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {correctionInvoices.map((correctionInvoice) => (
                <tr key={correctionInvoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {correctionInvoice.correctionNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(correctionInvoice.correctionDate).toLocaleDateString('pl-PL')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getTypeText(correctionInvoice.correctionType)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(correctionInvoice.status)}`}>
                      {getStatusText(correctionInvoice.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={correctionInvoice.differenceAmount >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(correctionInvoice.differenceAmount, correctionInvoice.currency)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                    {correctionInvoice.reason}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center justify-end gap-1">
                      {/* Кнопка просмотра PDF */}
                      <button
                        onClick={() => handleViewPdf(correctionInvoice)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title={t('invoices.viewPdf') || 'Просмотр PDF'}
                      >
                        <span className="text-base">👁️</span>
                      </button>
                      {/* Кнопка скачивания PDF */}
                      <button
                        onClick={() => handleDownloadPdf(correctionInvoice.id!, correctionInvoice.correctionNumber, correctionInvoice.clientName)}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                        title={t('invoices.download') || 'Скачать'}
                      >
                        <span className="text-base">⬇️</span>
                      </button>
                    
                    {correctionInvoice.status === CorrectionInvoiceStatus.DRAFT && (
                      <>
                        <select
                          value={correctionInvoice.status}
                          onChange={(e) => handleStatusChange(correctionInvoice.id!, e.target.value as CorrectionInvoiceStatus)}
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                        >
                          <option value={CorrectionInvoiceStatus.DRAFT}>{t('correctionInvoices.statuses.DRAFT')}</option>
                          <option value={CorrectionInvoiceStatus.SENT}>{t('correctionInvoices.statusOptions.send')}</option>
                        </select>
                        <button
                          onClick={() => handleDelete(correctionInvoice.id!)}
                          className="text-red-600 hover:text-red-900 text-xs"
                        >
                          {t('correctionInvoices.delete')}
                        </button>
                      </>
                    )}
                    {correctionInvoice.status === CorrectionInvoiceStatus.SENT && (
                      <select
                        value={correctionInvoice.status}
                        onChange={(e) => handleStatusChange(correctionInvoice.id!, e.target.value as CorrectionInvoiceStatus)}
                        className="text-xs border border-gray-300 rounded px-2 py-1"
                      >
                        <option value={CorrectionInvoiceStatus.SENT}>{t('correctionInvoices.statuses.SENT')}</option>
                        <option value={CorrectionInvoiceStatus.ACCEPTED}>{t('correctionInvoices.statusOptions.accept')}</option>
                        <option value={CorrectionInvoiceStatus.REJECTED}>{t('correctionInvoices.statusOptions.reject')}</option>
                      </select>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Пагинация */}
        {!originalInvoiceId && totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {t('correctionInvoices.pagination.showing')} {correctionInvoices.length} {t('correctionInvoices.pagination.of')} {totalElements} {t('correctionInvoices.pagination.records')}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.previous')}
              </button>
              <span className="px-3 py-1 text-sm">
                {currentPage + 1} {t('correctionInvoices.pagination.of')} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                disabled={currentPage === totalPages - 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Модальное окно просмотра PDF */}
      {selectedCorrectionInvoice && (
        <CorrectionPdfViewer
          companyId={companyId}
          correctionInvoiceId={selectedCorrectionInvoice.id!}
          correctionNumber={selectedCorrectionInvoice.correctionNumber}
          clientName={selectedCorrectionInvoice.clientName}
          pdfLanguage={selectedCorrectionInvoice.pdfLanguage}
          isOpen={pdfViewerOpen}
          onClose={() => {
            setPdfViewerOpen(false);
            setSelectedCorrectionInvoice(null);
          }}
        />
      )}
    </div>
  );
};

// Компонент для просмотра PDF корректов
interface CorrectionPdfViewerProps {
  companyId: string;
  correctionInvoiceId: string;
  correctionNumber: string;
  clientName: string;
  pdfLanguage?: string;
  isOpen: boolean;
  onClose: () => void;
}

const CorrectionPdfViewer: React.FC<CorrectionPdfViewerProps> = ({
  companyId,
  correctionInvoiceId,
  correctionNumber,
  clientName,
  pdfLanguage,
  isOpen,
  onClose
}) => {
  const { language: systemLanguage } = useLanguage();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const initialLanguage = (pdfLanguage as Language) || systemLanguage || Language.PL_ONLY;
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(initialLanguage);

  React.useEffect(() => {
    if (isOpen) {
      if (pdfLanguage) {
        setSelectedLanguage(pdfLanguage as Language);
      } else {
        setSelectedLanguage(systemLanguage || Language.PL_ONLY);
      }
    }
  }, [isOpen, pdfLanguage, systemLanguage]);

  const loadPdf = React.useCallback(async (language: Language = selectedLanguage) => {
    setIsLoading(true);
    try {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      
      const url = `/api/companies/${companyId}/correction-invoices/${correctionInvoiceId}/pdf?language=${language}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, correctionInvoiceId, pdfUrl, selectedLanguage]);

  const downloadPdf = async () => {
    setIsLoading(true);
    try {
      await correctionInvoiceService.downloadCorrectionInvoicePdf(companyId, correctionInvoiceId, correctionNumber, clientName, selectedLanguage);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (language: Language) => {
    setSelectedLanguage(language);
    loadPdf(language);
  };

  React.useEffect(() => {
    if (isOpen && !pdfUrl) {
      loadPdf(selectedLanguage);
    }
  }, [isOpen, pdfUrl, selectedLanguage, loadPdf]);

  React.useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-lg shadow-xl flex flex-col"
        style={{
          width: 'min(90vw, 80vh * 0.707)',
          height: '80vh',
          maxWidth: '90vw',
          maxHeight: '80vh'
        }}
      >
        <div className="flex justify-between items-center p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('invoices.pdfViewer.title')} {correctionNumber}
          </h2>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">{t('invoices.pdfViewer.language')}:</label>
              <select
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value as Language)}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PDF_LANGUAGE_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.nativeName}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={downloadPdf}
                disabled={isLoading}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? t('invoices.pdfViewer.downloading') : t('invoices.pdfViewer.download')}
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
              >
                {t('invoices.pdfViewer.close')}
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {isLoading && !pdfUrl ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">{t('invoices.pdfViewer.loading')}</p>
              </div>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title={`PDF ${t('invoices.pdfViewer.title').toLowerCase()} ${correctionNumber}`}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-gray-600 mb-4">{t('invoices.pdfViewer.notLoaded')}</p>
                <button
                  onClick={() => loadPdf()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('invoices.pdfViewer.loadPdf')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorrectionInvoiceList;

